import React, { FC } from 'react';
import { useSelect } from 'downshift';
import Icon, { IconIds } from 'src/common/components/IconDS22';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import Typography from 'src/common/components/Typography';
import { useTranslation } from 'next-i18next';
import TripCartAttention from 'src/trip-cart/components/TripCartAttention';
import { TripCartAttentionField } from 'src/listing/types';
import styles from './TripCartDropdown.module.scss';

export type TripCartDropdownOption<T extends string = string> = {
  id: T;
  label: string;
  icon: IconIds;
  hasInstabook?: boolean;
  disabled?: boolean;
};

export type Props<T extends string = string> = {
  id?: string;
  options: TripCartDropdownOption<T>[];
  selectedOption: TripCartDropdownOption<T>;
  onSelect: (option: TripCartDropdownOption<T>) => void;
  align?: 'left' | 'right';
  attentionField?: TripCartAttentionField;
};

const TripCartDropdown: FC<Props<string>> = ({
  id,
  options,
  selectedOption,
  onSelect,
  align = 'left',
  attentionField,
}) => {
  const [t] = useTranslation('listing-page');
  const showCaretIcon = options.length > 1;
  const dropdownSelect = useSelect({
    id,
    items: options,
    selectedItem: selectedOption,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem && !selectedItem.disabled) {
        onSelect(selectedItem);
      }
    },
  });

  const {
    isOpen,
    selectedItem,
    getItemProps,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
  } = dropdownSelect;

  const showMenu = isOpen && options.length > 1;
  return (
    <div className={styles.root}>
      {attentionField && <TripCartAttention field={attentionField} />}
      <button
        type="button"
        className={styles.button}
        disabled={options.length === 1}
        {...getToggleButtonProps()}
      >
        {selectedItem && <Icon id={selectedItem.icon} size="m" className={styles.icon} />}
        <Typography
          type="span"
          size="body-s"
          weight="semibold"
          className={styles.buttonText}
        >
          {selectedItem?.label}
        </Typography>
        {showCaretIcon && (
          <div className={getClassNames(styles, 'iconWrapper', classNames({
            active: isOpen,
          }))}
          >
            <Icon
              id="caret-down"
              size="s"
              className={styles.arrowDown}
            />
          </div>
        )}
      </button>
      <ul
        className={getClassNames(styles, 'menu', classNames(align, { show: showMenu }))}
        {...getMenuProps()}
      >
        {options.map((option: TripCartDropdownOption, index: number) => (
          <li
            key={option.id}
            className={getClassNames(styles, 'menuItem', classNames({
              highlighted: highlightedIndex === index,
            }))}
            {...getItemProps({
              item: option,
              index,
            })}
          >
            <div className={styles.itemWrapper}>

              <div className={getClassNames(styles, 'itemContent', classNames({
                disabled: option.disabled,
              }))}
              >
                <Icon id={option.icon} size="m" />
                <div className={getClassNames(styles, 'description', classNames({
                  disabled: option.disabled,
                }))}
                >
                  <Typography
                    type="span"
                    size="body-m"
                    weight="semibold"
                  >
                    {option.label}
                  </Typography>
                  {option.hasInstabook && (
                    <div className={getClassNames(styles, 'instabook', classNames({
                      disabled: option.disabled,
                    }))}
                    >
                      <Icon
                        id="instabook-filled"
                        size="xs"
                      />
                      <Typography
                        type="span"
                        size="body-s"
                      >
                        {t('trip-cart--instabook-available')}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TripCartDropdown;
